import React, { useEffect, useState } from "react";
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getExpandedRowModel,
    flexRender,
    ExpandedState,
    OnChangeFn,
} from "@tanstack/react-table";
import { IRemitterProps } from "../../../pages/remitter/Remmiter";
import { RemitterBanner } from "../../../assets/Icons";
import RemitterDetails from "../../../pages/remitter/RemitterDetails";
import KycStatus from "../KycStatus";
import Typograpy from "../Typography";
import UpArrow from "../../../assets/svg/upward-arrow.svg";
import DownArrow from "../../../assets/svg/downward-arrow.svg";

interface Data {
    id: number;
    name: string;
    age: number;
    occupation: string;
}

interface TableWithDetailPanelProps {
    data: IRemitterProps.remitterData[];
    deleteRemitterHandler: (id: any) => void;
    handleTransactionUpdate: () => void;
    handleSelectedRemitter: (id: any) => void;
    handleResendKycLink: (id: string) => void;
    handleResendLink: (number: any) => void;
    catchKycLink?: string | undefined;
    showToast?: (message: string, type: "success" | "failure") => void;
    datFetchFailure?: boolean
    setSelectedRow: React.Dispatch<React.SetStateAction<number | undefined>>
    setRemitterInfo: React.Dispatch<React.SetStateAction<any>>
    selectRemitterHandler: (SelectedId: number) => void
    selectedRemitter: any
    setSelectedRemitter: React.Dispatch<any>
    selectedRow: any;
    collapseTrigger: boolean
}

const columnHelper = createColumnHelper<IRemitterProps.remitterData>();

const columns = [
    columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
        header: "Remitter Name",
        enableSorting: true,
    }),
    columnHelper.accessor("email", {
        header: "Remitter email",
        enableSorting: true,

    }),
    columnHelper.accessor("phoneNumber", {
        header: "Remitter Mobile",
        enableSorting: true,
    }),
    columnHelper.accessor("kycStatus", {
        header: "KYC Status",
        cell: (info) => {
            const rowData = info.row.original;
            return rowData?.id !== null && (
                <KycStatus kycStatus={rowData?.kycStatus} />
            )
        },
        enableSorting: true,
    }),
    columnHelper.accessor("panNumber", {
        header: "Remitter PAN",
        enableSorting: true,
    }),
];

const TableWithDetailPanel: React.FC<TableWithDetailPanelProps> = ({
    data,
    deleteRemitterHandler,
    handleTransactionUpdate,
    handleResendKycLink,
    handleSelectedRemitter,
    handleResendLink,
    catchKycLink,
    showToast,
    datFetchFailure,
    setSelectedRow,
    setRemitterInfo,
    selectRemitterHandler,
    selectedRemitter,
    setSelectedRemitter,
    selectedRow,
    collapseTrigger
}) => {
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const handleExpandedChange: OnChangeFn<ExpandedState> = (updaterOrValue) => {
        setExpanded((prev) =>
            typeof updaterOrValue === "function"
                ? updaterOrValue(prev)
                : updaterOrValue
        );
    };
    

    const tableInstance = useReactTable({
        data,
        columns,
        state: {
            expanded,
        },
        onExpandedChange: handleExpandedChange,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
    });

    const { getHeaderGroups, getRowModel } = tableInstance;

     useEffect(() => {
    // Called whenever collapseTrigger changes
    getRowModel().rows.forEach((row) => {
      if (row.getIsExpanded()) {
        row.toggleExpanded(false);
      }
    });
    setSelectedRow(undefined);
  }, [collapseTrigger]);

    // useEffect(() => {
    //     const firstRow = getRowModel().rows[0];
    //     if (isInitialLoad && firstRow) {
    //         const clickedRowId = firstRow.original?.id;
    //         setSelectedRemitter(firstRow.original);
    //         setSelectedRow(clickedRowId);
    //         firstRow.toggleExpanded(true);
    //         setRemitterInfo(firstRow.original);
    //         setIsInitialLoad(false);
    //     }
    // }, [getRowModel, isInitialLoad]);

    return (
        <div className="mt-3 flex flex-col items-center justify-center">
            {data?.length === 0 ? (
                <div className="flex flex-col items-center justify-center mt-[232px] mb-36">
                    <RemitterBanner />
                    <Typograpy
                        tagType="div"
                        text={
                            datFetchFailure
                                ? "Failed to fetch remitter details. Please click on the refresh button"
                                : "Its Empty Here, Let’s Get Started"
                        }
                        className="text-2xl font-normal text-grey-1550 mt-[61px] text-center"
                    />
                </div>
            ) : null}
            {data.length !== 0 && <table style={{ width: "100%",  maxWidth:"1200px"}}>
                <thead>
                    {getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id} >
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    style={{ padding: "10px", textAlign: "justify" }}
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody >
                    {getRowModel().rows.map((row, index) => (
                        <React.Fragment key={row.id}>
                            <tr
                                className="hover:bg-[#F0F5FF] cursor-pointer"
                                style={
                                    row.getIsExpanded()
                                        ? { backgroundColor: "#F0F5FF" } 
                                        : {}
                                }
                                onClick={() => {
                                    const clickedRowId = row.original?.id;
                                    if (selectedRow === clickedRowId) {
                                        row.toggleExpanded(false);
                                        setSelectedRow(undefined);
                                    } else {
                                        if (selectedRow !== null) {
                                            getRowModel().rows.forEach(r => {
                                                if (r.original.id === selectedRow && r.getIsExpanded()) {
                                                    r.toggleExpanded(false);
                                                }
                                            });
                                        }
                                        setSelectedRemitter(row.original);
                                        row.toggleExpanded(true);
                                        setSelectedRow(clickedRowId);
                                        setRemitterInfo(row.original);
                                    }
                                }}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td
                                        key={cell.id}
                                        style={{ padding: "10px" }}
                                        className="cursor-pointer"
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                                <td>
                                    <button
                                        className="hover:scale-150 transition-transform duration-200 mr-3"
                                    >
                                        <img
                                            src={row.getIsExpanded() ? UpArrow : DownArrow}
                                            alt="toggle"
                                        />
                                    </button>
                                </td>
                            </tr>

                            {/* Always show RemitterDetails for first row OR if row is expanded */}
                            {(row.getIsExpanded()) && (
                                <tr>
                                    <td colSpan={columns.length + 1}>
                                        <RemitterDetails
                                            remitterData={row.original}
                                            deleteRemitterHandler={deleteRemitterHandler}
                                            handleTransactionUpdate={handleTransactionUpdate}
                                            handleSelectedRemitter={handleSelectedRemitter}
                                            handleResendKycLink={handleResendKycLink}
                                            handleResendLink={handleResendLink}
                                            catchKycLink={catchKycLink}
                                            showToast={showToast}
                                            selectRemitterHandler={selectRemitterHandler}
                                        />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            }
        </div>
    );
};

export default TableWithDetailPanel;
